/**
 * OrderListContainer
 * --
 */
import React, { useState, useEffect } from 'react';
import OrderListPresenter from './OrderListPresenter';
import MessageAlert, { PAYMENT_STORE_ID, POINT_STORE_ID } from 'utils';
import { ParcelServiceApi, ShopOrderApi } from 'api';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import {
  usePaymentCancelAccount,
  usePaymentCancelCard,
  // usePaymentCancelCashbill,
  // usePaymentCancelMobile,
} from 'hooks/PaymentHooks.';
import {
  useCreateRefundDeliveryManual,
  useCreateRefundRentalManual,
  useCreateRefundSubscriptionManual,
} from 'hooks/RefundHooks';

const today = {
  start_date: moment().startOf('day').unix(),
  end_date: moment().endOf('day').unix(),
};

const initialSearchFilter = {
  keywordType: 'shop_order_id',
  keyword: '',
  // dateType: 'today',
  dateType: 'week', // [today, week, month, 3month]
  startDate: null,
  endDate: null,
};

/**----------------------------------------
 * [Component] 주문내역 - 컨테이너
 * ----------------------------------------
 */
const OrderListContainer = ({ defaultShopId, purchaseStatus, permission }) => {
  /* ===== ROUTE ===== */
  const { order_type } = useParams();
  // console.log('[OrderListContainer] order_type: ', order_type);
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const { cancelCard } = usePaymentCancelCard(); // 카드/빌링 결제 취소
  // const { cancelCashbill } = usePaymentCancelCashbill(); // 현금영수증 취소
  // const { cancelMobile } = usePaymentCancelMobile(); // 휴대폰 결제 취소
  const { cancelAccount } = usePaymentCancelAccount(); // 계좌이체 취소

  // 환불
  const { createRefundDeliveryManual } = useCreateRefundDeliveryManual();
  const { createRefundSubscriptionManual } =
    useCreateRefundSubscriptionManual();
  const { createRefundRentalManual } = useCreateRefundRentalManual();
  // const { refundChargeJmPay } = useRefundChargeJmPay();

  const refundMutate = {
    DELIVERY: createRefundDeliveryManual,
    SUBSCRIPTION: createRefundSubscriptionManual,
    RENTAL: createRefundRentalManual,
    STORE_PICKUP: createRefundDeliveryManual,
  };

  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);

  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  /**--------------------
   * 필터 초기화
   * --------------------
   */
  const handleResetFilter = () => {
    setSearchFilter(initialSearchFilter);
    getOrders({
      ...initialSearchFilter,
      isReset: true,
    });
  };

  /**--------------------
   * 필터
   * --------------------
   */
  const handleChange = (key, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**--------------------
   * 전체 주문내역 조회
   * --------------------
   */
  const getOrders = async (f = null) => {
    setLoading(true);
    try {
      const filtered = f ? f : searchFilter;
      const {
        keywordType,
        keyword,
        startDate,
        endDate,
        dateType,
        isReset,
        ...rest
      } = filtered;
      let query = {
        ...today,
      };
      if (startDate && endDate) {
        query = {
          start_date: startDate,
          end_date: endDate,
        };
      } else {
        if (dateType === 'week') {
          query.start_date = moment().add(-7, 'days').startOf('day').unix();
        } else if (dateType === 'month') {
          query.start_date = moment().add(-30, 'days').startOf('day').unix();
        } else if (dateType === '3month') {
          query.start_date = moment().add(-90, 'days').startOf('day').unix();
        } else {
          query = {
            ...today,
          };
        }
      }

      const { status, message, data } = await ShopOrderApi.getShopOrders({
        ...query,
        join_tables: `["user", "shop_information"]`,
        ...(!isReset && currentShop
          ? { shop_information_id: currentShop }
          : isReset && defaultShopId
            ? { shop_information_id: defaultShopId }
            : {}),
      });

      const res = await ParcelServiceApi.getParcelServices({
        ...query,
        join_tables: `["refund"]`,
        ...(!isReset && currentShop
          ? { shop_information_id: currentShop }
          : isReset && defaultShopId
            ? { shop_information_id: defaultShopId }
            : {}),
        // 필터
        // ...(order_type && { purchase_status: order_type }),
        // ...(order_type && { deposit_status: order_type }),
      });

      if (status !== 200 || res?.status !== 200) throw { message };

      const newData = data
        ?.filter((item) => item?.type === 'DELIVERY' || item?.type === 'STORE_PICKUP')
        ?.map((_item) => ({
          ..._item,
          parcel_service: res?.data?.filter(
            (parcel) => _item?.shop_order_id === parcel?.shop_order_id
          ),
        }));
      // 입금전 상태 필터
      const typeData = !order_type
        ? newData
        : newData?.filter((_item) =>
          _item?.parcel_service.some(
            (parcel) => parcel?.deposit_status === order_type
          )
        );

      /* 검색어 처리 로직 */
      // 데이터 담을 변수
      let data2 = [];
      // 키워드 찾기
      const filteredCall = (o) => `${o[keywordType]}`.indexOf(keyword) >= 0;
      // 하위 2차 뎁스 표현
      const mapCall = (o, key) => o[key].filter(filteredCall).length > 0 && o;

      // 검색어 필터
      if (
        keywordType === 'shop_order_id' ||
        keywordType === 'shop_information_name'
      ) {
        data2 = typeData?.filter(filteredCall);
      } else if (keywordType === 'product_name') {
        data2 = typeData?.filter((o) => mapCall(o, 'product_information'));
      } else if (keywordType === 'user_name' || keywordType === 'user_phone') {
        data2 = typeData?.filter((o) => filteredCall(o['user']));
      }

      // 남은 검색 필터 처리
      const restFilters = Object.entries(rest).filter(([_, value]) => value);

      if (restFilters.length) {
        data2 = restFilters.reduce(
          (acc, [key, value]) =>
            acc.filter((item) => {
              if (value === 'PARTIALLY_REFUNDED') {
                return ['PARTIALLY_REFUNDED', 'REFUNDED'].includes(item[key]);
              }
              return item[key] === value;
            }),
          [...data2]
        );
      }

      setTimeout(() => {
        // if (data2.length < 1) MessageAlert.info('검색된 데이터가 없습니다.');
        setOrderList(
          data2.map((item) => ({
            ...item,
            // parcel_service: item.parcel_service[0],
            parcel_service: item.parcel_service,
          }))
        );
        setLoading(false);
      }, 1000);
    } catch (e) {
      // console.log('Error: ', e);
      setTimeout(() => {
        setLoading(false);
        MessageAlert.error(e.message);
      }, 2500);
    }
  };
  /**--------------------
   * 주문 취소
   * --------------------
   * @param {object} refund 환불 정보
   * @param {string} type 결제 수단
   */
  const handleCancel = async (refund, type = 'CARD') => {
    /**NOTE - refund
     * trno[string]: payment_info의 trno (JM_PAY가 아닐 경우 필수)
     * cancelTaxFreeAmount[string | number]: 취소 면세금액
     * 예시) 총금액 1만원 부분취소 요청
            과세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=0
            면세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=10000
            일부(3천원) 면세상품인 경우 refund_amount=10000, cancelTaxFreeAmount=3000
     * cancelSeq: 취소 순서 (최초 1) refund가 일어난 횟수 + 1 (JM_PAY가 아닐 경우 필수)
     *
     * refund_amount[number]: 환불금액 (필수)
     * refund_num[number]: 환불수량 (필수)
     * is_coupon_refund[string]: 쿠폰환불 여부 (필수)
     * refund_reason[string]: 환불사유
     *
     * parcel_service_id[number]: 배송, 정기배송일 경우 필수
     * regular_payment_id: 정기배송일 경우 필수
     * rental_id: 렌탈일 경우 필수
     * product_type: DELIVERY | SUBSCRIPTION | RENTAL
     */
    let success = false;
    try {
      setLoading(true);
      const paymentType = type.toLocaleUpperCase();

      const {
        trno,
        cancelTaxFreeAmount,
        cancelSeq,
        product_type,
        refund_amount,
        card_id,
        ...rest
      } = refund;

      const cancelData = {
        mid: card_id ? POINT_STORE_ID : PAYMENT_STORE_ID,
        orgTradeKeyType: 'TID',
        orgTradeKey: trno,
        cancelType: 'PARTIAL',
        cancelTotalAmount: `${refund_amount}`,
        cancelTaxFreeAmount: `${cancelTaxFreeAmount ?? 0}`,
        cancelSeq: `${cancelSeq ?? 1}`,
      };

      const refundData = {
        ...rest,
        memo: rest?.refund_reason,
        refund_amount,
        refund_status: 'COMPLETED',
      };

      let cancelled, refunded;
      switch (paymentType) {
        case 'MOBILE': // 휴대폰 결제
          MessageAlert.warning('휴대폰 결제는 취소 불가능합니다!');
          return false;
        case 'TRANSFER': // TODO: 계좌이체 (테스트 필요)
          // 계좌이체 결제 취소
          cancelled = await cancelAccount(cancelData);

          if (cancelled.status !== 200 || cancelled.data.code !== 'A0200') {
            MessageAlert.info('결제 취소에 실패하였습니다.');
            return false;
          }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: cancelled.data.data,
          });
          break;
        case 'JM_PAY': // 제이엠페이 (테스트 필요)
          // JM페이 환불
          // const { status } = await refundChargeJmPay({
          //   user_id: rest?.user_id,
          //   price: refund_amount,
          // });

          // if (status !== 200) {
          //   MessageAlert.info('JM Pay 환불에 실패하였습니다.');
          //   return;
          // }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: null,
          });

          break;
        case 'CARD':
        case 'NAVER_PAY':
        case 'KAKAO_PAY':
          // 카드 결제 취소
          cancelled = await cancelCard(cancelData);

          if (cancelled.status !== 200 || cancelled.data.code !== 'A0200') {
            MessageAlert.info('결제 취소에 실패하였습니다.');
            return false;
          }

          // 환불 데이터 생성
          refunded = await refundMutate[product_type]({
            ...refundData,
            refund_payment_info: cancelled.data.data,
          });

          break;
        default:
          MessageAlert.warning('알 수 없는 결제 타입!');
          return false;
      }

      if (refunded.status !== 200) {
        MessageAlert.info('환불 데이터 생성에 실패하였습니다.');
        return false;
      }

      MessageAlert.success('결제가 성공적으로 취소되었습니다.');
      success = true;
      return success;
    } catch (err) {
      console.error('[handleCancel] Error!! ', err);
      MessageAlert.error('결제 취소에 실패하였습니다.');
    } finally {
      setTimeout(() => {
        setLoading(false);
        return success;
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**--------------------
   * 전체 주문내역 조회
   * --------------------
   */
  useEffect(() => {
    getOrders();
  }, []);

  // console.log('purchaseStatus: ', purchaseStatus);
  // console.log('orderList: ', orderList);

  /* ===== RENDER ===== */
  const props = {
    permission,
    loading,
    orderList:
      purchaseStatus === null
        ? orderList
        : orderList
          .map((item) => ({
            ...item,
            parcel_service: item.parcel_service.filter((sub) =>
              purchaseStatus.includes(sub.delivery_status) || sub.delivery_status === "RETURN_COMPLETED"
            ),
          }))
          .filter((item) => item.parcel_service.length),
    searchFilter,
    handleChange,
    onResetFilter: handleResetFilter,
    getOrders,
    currentShop,
    setCurrentShop,
    onCancel: handleCancel,
    order_type,
  };
  return (
    <>
      <OrderListPresenter {...props} />
    </>
  );
};

/* DF */
OrderListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
  paymentStatus: null, // ["COMPLETED", "PARTIALLY_REFUNDED"]
  purchaseStatus: null, // [PENDING, CONFIRMED, REFUNDED, RETURNED, CALCULATE]
};
export default OrderListContainer;
